import * as THREE from 'three'
import { CONFIG } from '../../config.js'
import { Tween, Group, Easing } from '@tweenjs/tween.js'
import { updatePanelContent } from '../setup.js'

const PART_DATA = {
    // Left Hemisphere
    "Frontal_Lobe_L": { title: "Frontal Lobe (Left)", description: "Controls voluntary movement, expressive language, and higher-level functions." },
    "Parietal_Lobe_L": { title: "Parietal Lobe (Left)", description: "Processes sensory information regarding the location of parts of the body." },
    "Occipital_Lobe_L": { title: "Occipital Lobe (Left)", description: "The visual processing center of the mammalian brain containing most of the anatomical region of the visual cortex." },
    "Temporal_Lobe_L": { title: "Temporal Lobe (Left)", description: "Involved in processing sensory input into derived meanings for retention of visual memory, language comprehension, and emotion association." },
    // Right Hemisphere
    "Frontal_Lobe_R": { title: "Frontal Lobe (Right)", description: "Controls voluntary movement, expressive language, and higher-level functions." },
    "Parietal_Lobe_R": { title: "Parietal Lobe (Right)", description: "Processes sensory information regarding the location of parts of the body." },
    "Occipital_Lobe_R": { title: "Occipital Lobe (Right)", description: "The visual processing center of the mammalian brain containing most of the anatomical region of the visual cortex." },
    "Temporal_Lobe_R": { title: "Temporal Lobe (Right)", description: "Involved in processing sensory input into derived meanings for retention of visual memory, language comprehension, and emotion association." },
    // Internal Structures
    "Cerebellum": { title: "Cerebellum", description: "Receives information from the sensory systems, the spinal cord, and other parts of the brain and then regulates motor movements." },
    "Brain_Stem": { title: "Brain Stem", description: "Controls the flow of messages between the brain and the rest of the body." },
    "Brain_Stem_Short": { title: "Brain Stem", description: "Controls the flow of messages between the brain and the rest of the body." },

    // Additional Parts
    "Pons": { title: "Pons", description: "Handles unconscious processes and communication between brain parts." },
    "Midbrain": { title: "Midbrain", description: "Key for motor movement, particularly of the eye, and auditory/visual processing." },
    "Hippocampus": { title: "Hippocampus", description: "Critical for the formation of new memories and spatial navigation." },
    "Basal_Ganglia": { title: "Basal Ganglia", description: "Regulates control of voluntary motor movements." },
    "Ventricles": { title: "Ventricles", description: "Cavities filled with cerebrospinal fluid to cushion the brain." },
    "Olfactory_Bulb": { title: "Olfactory Bulb", description: "Processes information about odors." },
    "Corpus_Callosum": { title: "Corpus Callosum", description: "Connects the left and right hemispheres, facilitating communication." },

    // Potentially split parts (Adding aliases just in case)
    "Hippocampus_L": { title: "Hippocampus (Left)", description: "Critical for the formation of new memories and spatial navigation." },
    "Hippocampus_R": { title: "Hippocampus (Right)", description: "Critical for the formation of new memories and spatial navigation." },
    "Basal_Ganglia_L": { title: "Basal Ganglia (Left)", description: "Regulates control of voluntary motor movements." },
    "Basal_Ganglia_R": { title: "Basal Ganglia (Right)", description: "Regulates control of voluntary motor movements." },
    "Olfactory_Bulb_L": { title: "Olfactory Bulb (Left)", description: "Processes information about odors." },
    "Olfactory_Bulb_R": { title: "Olfactory Bulb (Right)", description: "Processes information about odors." },
    "Corpus_Callosum_R": { title: "Corpus Callosum", description: "Connects the left and right hemispheres, facilitating communication." },
    "Corpus_Callosum_L": { title: "Corpus Callosum", description: "Connects the left and right hemispheres, facilitating communication." }
};

export const config = {
    ...CONFIG,
};

export function init(scene, camera, renderer, controls, gui) {


    // Start with hotspots hidden (until zoom)
    if (window.SceneManager && window.SceneManager.enableHotspots) {
        window.SceneManager.enableHotspots(false);
    }

    // MOBILE OPTIMIZATION: Disable Pinch Zoom & Add Button
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    let mobileZoomBtn = null;
    let isExplorationStarted = !isMobile;
    let interactionBlockedUntil = 0; // State to block re-clicking immediately after closing panel

    // REPLACEMENT: Manually handle Close Button to bypass SceneManager reset
    const closeBtn = document.getElementById('close-panel');
    const panel = document.getElementById('info-panel');

    // Safety check - if setup.js runs later, this might be overwritten, 
    // but usually setup runs before init.
    if (closeBtn && panel) {
        // Remove existing listeners by cloning
        const newCloseBtn = closeBtn.cloneNode(true);
        closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn);

        // Add custom listener for Scene 1
        newCloseBtn.addEventListener('click', () => {
            console.log("Custom Close - Scene 1");
            panel.classList.remove('active');

            // Clear highlights/hover
            if (window.SceneManager && window.SceneManager.setOutlineObjects) {
                window.SceneManager.setOutlineObjects([]);
            }
            hoveredPart = null;

            // INVALIDATE MOUSE to prevent "ghost" selections
            mouse.x = -Infinity;
            mouse.y = -Infinity;
            hasMouseMoved = false;

            // DO NOT call window.SceneManager.onPanelClosed() or resetView()

            // Block further interaction for 200ms
            interactionBlockedUntil = Date.now() + 200;
        });
    }


    if (isMobile && controls) {
        controls.enableZoom = false; // Disable 2-finger zoom to prevent issues


        // Create Button
        mobileZoomBtn = document.createElement('button');
        mobileZoomBtn.innerText = "Start Exploration";
        mobileZoomBtn.className = "mobile-zoom-btn"; // Will style in CSS
        document.body.appendChild(mobileZoomBtn);

        mobileZoomBtn.addEventListener('click', () => {
            // Treat click as auto-zoom trigger
            if (!hasTriggeredZoom) {
                hasTriggeredZoom = true;
                isExplorationStarted = true; // Enable interaction
                performAutoZoom();
                mobileZoomBtn.style.display = 'none'; // Hide after click
            }
        });
    }

    const cleanups = [];
    const updatables = [];
    const tweenGroup = new Group();

    // Interaction State
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    let hoveredPart = null;

    // Configuration for interactions
    const ROTATION_SPEED = 0.2; // Radians per second
    const ROTATION_FULL_DIST = 500;
    const ROTATION_STOP_DIST = 350;

    // Auto-Zoom Configuration
    const AUTO_ZOOM_TRIGGER_DIST = 610;
    // Exact "Inspection View" Coordinates (Side Profile)
    const AUTO_ZOOM_POS = { x: -250, y: 50, z: 0 };
    const AUTO_ZOOM_LOOKAT = { x: 0, y: 20, z: 0 };

    const LEFT_BRAIN_PARTS = ["Frontal_Lobe_L", "Parietal_Lobe_L", "Occipital_Lobe_L", "Temporal_Lobe_L"];

    // Visibility Threshold
    const HIDE_THRESHOLD = 610; // Triggers immediately when auto-zoom starts

    // State
    let isLeftHidden = false;
    let hasTriggeredZoom = false;
    let lastRaycast = 0;

    let interactables = [];
    let hasMouseMoved = false;
    let isControlInteracting = false;

    // --- INTERACTION HANDLERS ---
    function onMouseMove(event) {
        // Lightweight mouse update
        mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
        hasMouseMoved = true;
    }

    let hoverTimeout = null;

    function checkIntersection() {
        const model = window.SceneManager ? window.SceneManager.loadedModel : null;
        // Disable interaction during auto-zoom
        if (!model || controls.enabled === false || !hasMouseMoved || isControlInteracting) return;

        // BLOCK if interaction is temporarily blocked
        if (Date.now() < interactionBlockedUntil) return;

        hasMouseMoved = false;

        // Lazy init
        if (interactables.length === 0) {
            model.traverse((child) => {
                if (child.isMesh) {
                    interactables.push(child);
                }
            });
        }

        if (interactables.length === 0) return;

        raycaster.setFromCamera(mouse, camera);
        const intersects = raycaster.intersectObjects(interactables, false).filter(hit => hit.object.visible === true);

        if (intersects.length > 0) {
            let object = intersects[0].object;

            if (hoveredPart !== object) {
                hoveredPart = object;
                document.body.style.cursor = 'pointer';


                if (controls) controls.dampingFactor = 0.2;

                if (window.SceneManager && window.SceneManager.setOutlineObjects) {
                    window.SceneManager.setOutlineObjects([hoveredPart]);
                }

                // Clear any pending timeout
                if (hoverTimeout) clearTimeout(hoverTimeout);

                // Hover should only highlight AND show panel passively (User request)
                hoverTimeout = setTimeout(() => {
                    if (hoveredPart === object && window.SceneManager && window.SceneManager.openPanelForPart) {
                        const data = PART_DATA[hoveredPart.name];
                        window.SceneManager.openPanelForPart(hoveredPart.name, { onlyPanel: true, data: data });
                    }
                }, 100);
            }
        } else {
            if (hoveredPart) {
                hoveredPart = null;
                document.body.style.cursor = 'default';
                if (controls) controls.dampingFactor = 0.05;
                if (window.SceneManager && window.SceneManager.setOutlineObjects) {
                    window.SceneManager.setOutlineObjects([]);
                }
                if (hoverTimeout) clearTimeout(hoverTimeout);
            }
        }

    }

    function handleInteraction(event) {
        // IGNORE touches/clicks on UI elements (only accept canvas)
        if (event.target && !event.target.classList.contains('webgl')) return;

        // BLOCK interaction on mobile if not started
        if (isMobile && !isExplorationStarted) return;

        // BLOCK if interaction is temporarily blocked (e.g. just closed a panel)
        if (Date.now() < interactionBlockedUntil) return;

        // Determine coordinates
        let clientX, clientY;
        let isTouch = false;

        if (event.changedTouches && event.changedTouches.length > 0) {
            clientX = event.changedTouches[0].clientX;
            clientY = event.changedTouches[0].clientY;
            isTouch = true;
        } else {
            clientX = event.clientX;
            clientY = event.clientY;
        }

        // For touch, OR if hoveredPart is null (safety), perform a direct raycast
        if (isTouch || !hoveredPart) {
            mouse.x = (clientX / window.innerWidth) * 2 - 1;
            mouse.y = -(clientY / window.innerHeight) * 2 + 1;

            raycaster.setFromCamera(mouse, camera);
            // Ensure interactables are populated
            if (interactables.length === 0) {
                const model = window.SceneManager ? window.SceneManager.loadedModel : null;
                if (model) {
                    model.traverse((child) => {
                        if (child.isMesh) interactables.push(child);
                    });
                }
            }

            const intersects = raycaster.intersectObjects(interactables, false).filter(hit => hit.object.visible === true);
            if (intersects.length > 0) {
                hoveredPart = intersects[0].object;
            } else {
                hoveredPart = null;
            }
        }

        if (hoveredPart) {
            // Helper: Open Associated Panel via SceneManager
            if (window.SceneManager && window.SceneManager.openPanelForPart) {
                const data = PART_DATA[hoveredPart.name];
                window.SceneManager.openPanelForPart(hoveredPart.name, { onlyPanel: true, data: data });
            }

            // 1. Highlight
            if (window.SceneManager && window.SceneManager.setOutlineObjects) {
                window.SceneManager.setOutlineObjects([hoveredPart]);
            }
        } else {
            // Clicked on nothing? Clear highlight
            if (window.SceneManager && window.SceneManager.setOutlineObjects) {
                window.SceneManager.setOutlineObjects([]);
            }
        }
    }

    function onControlStart() { isControlInteracting = true; }
    function onControlEnd() { isControlInteracting = false; }

    // Register Listeners
    if (controls) {
        controls.addEventListener('start', onControlStart);
        controls.addEventListener('end', onControlEnd);
    }
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('click', handleInteraction);
    window.addEventListener('touchstart', handleInteraction, { passive: false });
    cleanups.push(() => {
        // Clear any pending hover effects
        if (hoverTimeout) clearTimeout(hoverTimeout);

        if (controls) {
            controls.removeEventListener('start', onControlStart);
            controls.removeEventListener('end', onControlEnd);
        }
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('click', handleInteraction);
        window.removeEventListener('touchstart', handleInteraction);
        document.body.style.cursor = 'default';
        if (window.SceneManager && window.SceneManager.setOutlineObjects) {
            window.SceneManager.setOutlineObjects([]);
        }
        if (mobileZoomBtn) {
            mobileZoomBtn.remove();
        }
        // Restore Standard Close Button Behavior
        const closeBtnClean = document.getElementById('close-panel');
        if (closeBtnClean) {
            const stdCloseBtn = closeBtnClean.cloneNode(true);
            closeBtnClean.parentNode.replaceChild(stdCloseBtn, closeBtnClean);

            stdCloseBtn.addEventListener('click', () => {
                document.getElementById('info-panel').classList.remove('active');
                if (window.SceneManager && window.SceneManager.onPanelClosed) {
                    window.SceneManager.onPanelClosed();
                }
            });
        }
    });

    // Animation Loop
    updatables.push((delta, elapsedTime) => {
        tweenGroup.update(); // Update tweens

        // Access the loaded model from the global manager (since it loads async)
        const model = window.SceneManager ? window.SceneManager.loadedModel : null;

        if (model) {
            // 0. Throttled Raycast
            // Throttle to 10 FPS (0.1s)
            if (elapsedTime - lastRaycast > 0.1) {
                lastRaycast = elapsedTime;
                checkIntersection();
            }

            // 1. Calculate Distance
            const distance = camera.position.distanceTo(controls.target);

            // 2. Auto-Zoom Logic - Triggers once when user zooms in
            // If user enters trigger zone and hasn't triggered yet, animate in
            if (distance < AUTO_ZOOM_TRIGGER_DIST && !hasTriggeredZoom && distance > 260) {
                hasTriggeredZoom = true;
                performAutoZoom();
            }
            // Don't reset trigger - we only want this to happen once per scene load

            // 3. Auto-Rotation Logic (Smooth Slowdown)
            let speedFactor = 0;
            if (distance >= ROTATION_FULL_DIST) speedFactor = 1;
            else if (distance <= ROTATION_STOP_DIST) speedFactor = 0;
            else {
                speedFactor = (distance - ROTATION_STOP_DIST) / (ROTATION_FULL_DIST - ROTATION_STOP_DIST);
            }

            // Should rotation continue during auto-zoom? 
            // STOP rotation immediately if zoom triggered to avoid drifting view
            // STOP rotation if user is hovering over a part (interactive pause)
            if (speedFactor > 0 && !hasTriggeredZoom && !hoveredPart) {
                model.rotation.y += ROTATION_SPEED * speedFactor * delta;
            }

            // 4. Proximity Hiding Logic (Instant Toggle)
            if (distance < HIDE_THRESHOLD) {
                if (!isLeftHidden) {
                    toggleLeftBrain(model, false);
                    isLeftHidden = true;
                }
            } else {
                if (isLeftHidden) {
                    toggleLeftBrain(model, true);
                    isLeftHidden = false;
                }
            }

            // 5. Auto-Reset on Zoom Out
            // If user zooms out far enough, automatically return to start state
            if (distance > 650 && hasTriggeredZoom && controls.enabled) {

                resetView();
            }
        }
    });

    function performAutoZoom() {

        controls.enabled = false; // Disable manual control

        // Ensure consistent orientation by resetting rotation to 180 (Math.PI)
        const model = window.SceneManager ? window.SceneManager.loadedModel : null;
        if (model) {
            // Smart Rotation: Tween to closest PI 180 degrees (Side profile)
            const current = model.rotation.y;
            // Calculate closest equivalent of PI
            const k = Math.round((current - Math.PI) / (Math.PI * 2));
            const target = Math.PI + k * (Math.PI * 2);

            new Tween(model.rotation, tweenGroup)
                .to({ y: target }, 1500)
                .easing(Easing.Quadratic.Out)
                .start();
        }

        // Animate Camera Position
        new Tween(camera.position, tweenGroup)
            .to({ x: AUTO_ZOOM_POS.x, y: AUTO_ZOOM_POS.y, z: AUTO_ZOOM_POS.z }, 2000) // 2s smooth transition
            .easing(Easing.Quadratic.InOut)
            .start();

        if (window.SceneManager && window.SceneManager.enableHotspots) {
            window.SceneManager.enableHotspots(false);
        }

        // Animate Camera LookAt (Controls Target)
        new Tween(controls.target, tweenGroup)
            .to({ x: AUTO_ZOOM_LOOKAT.x, y: AUTO_ZOOM_LOOKAT.y, z: AUTO_ZOOM_LOOKAT.z }, 2000)
            .easing(Easing.Quadratic.InOut)
            .onComplete(() => {
                controls.enabled = true; // Return control
                if (window.SceneManager && window.SceneManager.enableHotspots) {
                    window.SceneManager.enableHotspots(true);
                }
            })
            .start();
    }

    function toggleLeftBrain(model, visible) {
        let count = 0;
        model.traverse((child) => {
            // Broaden check to any part ending in _L or in the specific list
            if (child.name && (child.name.endsWith('_L') || LEFT_BRAIN_PARTS.includes(child.name))) {
                child.visible = visible;
                count++;
            }
        });

    }



    function resetView() {

        // Don't reset hasTriggeredZoom - keep it true so auto-zoom won't trigger again
        controls.enabled = false; // Disable during animation

        if (window.SceneManager && window.SceneManager.enableHotspots) {
            window.SceneManager.enableHotspots(false);
        }

        const model = window.SceneManager ? window.SceneManager.loadedModel : null;
        if (model) {
            // Reset visibility
            toggleLeftBrain(model, true);
            isLeftHidden = false;
        }

        // Reset to FAR initial view (distance > 610 to prevent auto-zoom)
        const startPos = { x: -426, y: 256, z: 361 };
        const startTarget = { x: 0, y: 0, z: 0 };

        new Tween(camera.position, tweenGroup)
            .to(startPos, 1500)
            .easing(Easing.Quadratic.InOut)
            .start();

        new Tween(controls.target, tweenGroup)
            .to(startTarget, 1500)
            .easing(Easing.Quadratic.InOut)
            .onComplete(() => {
                controls.enabled = true;
                // Reset zoom trigger so rotation can resume and auto-zoom can happen again
                hasTriggeredZoom = false;

                // Show button again if mobile
                if (mobileZoomBtn) {
                    mobileZoomBtn.style.display = 'block';
                }
            })
            .start();
    }

    // Track zoom state for instruction text
    let hasZoomedIn = false;
    const zoomThreshold = config.scenes["Scene 1"].ui?.topInstructionText?.zoomThreshold || 400;

    // Add zoom detection to updatables
    updatables.push((delta, elapsed) => {
        const distance = camera.position.length();
        const topLabel = document.getElementById('top-instruction-label');

        if (topLabel && config.scenes["Scene 1"].ui?.topInstructionText) {
            const textConfig = config.scenes["Scene 1"].ui.topInstructionText;

            if (distance < zoomThreshold && !hasZoomedIn && textConfig.zoomedIn) {
                // User has zoomed in
                topLabel.textContent = textConfig.zoomedIn;
                hasZoomedIn = true;
            } else if (distance >= zoomThreshold && hasZoomedIn && textConfig.initial) {
                // User has zoomed back out
                topLabel.textContent = textConfig.initial;
                hasZoomedIn = false;
            }
        }
    });

    return {
        update: (delta, elapsed) => updatables.forEach(u => u(delta, elapsed)),
        dispose: () => cleanups.forEach(c => c()),
        reset: resetView
    };
}
